import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { API_URL, getCart, clearCart } from "../utils/cartApi";

const Checkout = () => {
  const navigate = useNavigate();
  const [cartItems, setCartItems] = useState([]);
  const [submitting, setSubmitting] = useState(false);
  const [razorpayLoaded, setRazorpayLoaded] = useState(false);

  const [formData, setFormData] = useState({
    fullName: "",
    phone: "",
    email: "",
    pincode: "",
    state: "",
    address: ""
  });

  const [error, setError] = useState("");

  useEffect(() => {
    const loadCart = async () => {
      const cart = await getCart();
      setCartItems(cart.items || []);
    };

    const script = document.createElement("script");
    script.src = "https://checkout.razorpay.com/v1/checkout.js";
    script.onload = () => setRazorpayLoaded(true);
    script.onerror = () => setError("Unable to load the payment system. Please try again.");
    document.body.appendChild(script);

    loadCart();

    return () => {
      script.remove();
    };
  }, []);

  const total = cartItems.reduce((a, c) => a + c.price * c.quantity, 0);

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const MIN_ORDER = 5000;

  const handlePayment = async () => {
    setError("");

    if (!formData.fullName || !formData.phone || !formData.state || !formData.address) {
      setError("Please fill all required fields");
      return;
    }

    if (total < MIN_ORDER) {
      setError(`Minimum order must be ₹${MIN_ORDER}`);
      return;
    }

    if (!razorpayLoaded) {
      setError("Payment system loading...");
      return;
    }

    setSubmitting(true);

    try {
      const totalAmount = Number(total);

      const orderResponse = await axios.post(`${API_URL}/api/payment/create-order`, {
        amount: totalAmount,
      });

      if (!orderResponse || !orderResponse.data || !orderResponse.data.order || !orderResponse.data.order.id) {
        alert("Backend failed to send a valid order_id. The keys might be mismatched.");
        setSubmitting(false);
        return;
      }

      const options = {
        key: String(process.env.REACT_APP_RAZORPAY_KEY_ID || process.env.VITE_RAZORPAY_KEY_ID).trim(),
        amount: orderResponse.data.order.amount,
        currency: orderResponse.data.order.currency,
        name: "Sri Dorasani Collections",
        description: "Saree Purchase",
        order_id: orderResponse.data.order.id,

        prefill: {
          name: formData.fullName,
          email: formData.email,
          contact: formData.phone
        },

        theme: {
          color: "#3d0c11"
        },

        handler: async function (response) {
  try {
    const verificationResponse = await axios.post(`${API_URL}/api/payment/verify-payment`, response);
    if (!verificationResponse.data.success) {
      throw new Error("Payment verification failed");
    }
  } catch (verificationError) {
    console.error("Payment verification failed:", verificationError.response?.data || verificationError.message);
    setError("Payment verification failed. Please contact support before retrying.");
    setSubmitting(false);
    return;
  }

  try {
    const orderData = {
      customer: {
        name: formData.fullName,
        phone: formData.phone,
        email: formData.email,
        pincode: formData.pincode,
        state: formData.state,
        address: formData.address
      },
      items: cartItems.map((item) => ({
        productId: item.productId || item._id || "",
        sareeId: item.sareeId || "",
        name: item.name,
        image: item.image || item.images?.[0] || "",
        price: Number(item.price),
        quantity: Number(item.quantity)
      })),
      subtotal: total,
      total,
      paymentId: response.razorpay_payment_id,
      razorpayOrderId: response.razorpay_order_id,
      status: "Paid"
    };

    const orderRes = await axios.post(`${API_URL}/api/orders`, orderData);
    console.log("✅ Order saved:", orderRes.data);

    await clearCart();
    window.dispatchEvent(new Event("cartUpdated"));
    alert("✅ Payment Successful & Order Placed");
    navigate("/");

  } catch (err) {
    console.error(
      "❌ Payment success but order save failed:",
      err.response?.data || err.message
    );

    try {
      const backupRes = await axios.post(`${API_URL}/api/orders/create`, {
        customer: {
          name: formData.fullName,
          phone: formData.phone,
          email: formData.email,
          pincode: formData.pincode,
          state: formData.state,
          address: formData.address
        },
        items: cartItems.map((item) => ({
          name: item.name,
          price: Number(item.price),
          quantity: Number(item.quantity)
        })),
        subtotal: total,
        total,
        paymentId: response.razorpay_payment_id,
        razorpayOrderId: response.razorpay_order_id,
        status: "Paid"
      });

      console.log("✅ Backup order saved:", backupRes.data);

      await clearCart();
      window.dispatchEvent(new Event("cartUpdated"));
      alert("✅ Payment Successful & Order Placed");
      navigate("/");

    } catch (err2) {
      console.error("❌ Backup also failed:", err2.message);
      alert("⚠️ Payment done but order saving failed. Contact admin.");
    }

  } finally {
    setSubmitting(false);
  }
},

        modal: {
          ondismiss: function () {
            setSubmitting(false);
          }
        }
      };

      if (!options.key) {
        setError("Razorpay key is missing. Check the frontend environment configuration.");
        setSubmitting(false);
        return;
      }

      const razorpay = new window.Razorpay(options);
      razorpay.on("payment.failed", function (response) {
        console.error("Payment Failed:", response.error);
        setError(response.error?.description || "Payment failed. Please try again.");
        setSubmitting(false);
      });
      razorpay.open();
    } catch (err) {
      console.error("❌ Payment order creation failed:", err.response?.data || err.message);
      setError(err.response?.data?.error || "Payment failed. Please try again.");
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-cover bg-repeat py-10 px-4 md:px-10" style={{ backgroundImage: "url('/bg-parchment.jpg')" }}>
      <div className="max-w-7xl mx-auto">

        <h1 className="text-3xl text-[#3d0c11] mb-8">Checkout</h1>

        <div className="grid lg:grid-cols-3 gap-10">

          {/* LEFT */}
          <div className="lg:col-span-2 bg-white p-8 shadow">

            <input
              name="fullName"
              placeholder="Full Name"
              onChange={handleChange}
              className="border p-3 w-full mb-3"
            />

            <input
              name="phone"
              placeholder="Phone"
              onChange={handleChange}
              className="border p-3 w-full mb-3"
            />

            <input
              name="email"
              placeholder="Email"
              onChange={handleChange}
              className="border p-3 w-full mb-3"
            />

            <input
              name="pincode"
              placeholder="Pincode"
              onChange={handleChange}
              className="border p-3 w-full mb-3"
            />

            <input
              name="state"
              placeholder="State"
              onChange={handleChange}
              className="border p-3 w-full mb-3"
            />

            <textarea
              name="address"
              placeholder="Address"
              onChange={handleChange}
              className="border p-3 w-full mb-4"
            />

            {error && <p className="text-red-600 mb-3">{error}</p>}

            <button
              onClick={handlePayment}
              disabled={submitting}
              className="w-full bg-[#3d0c11] text-white py-4"
            >
              {submitting ? "Processing..." : `Pay Online • ₹${total}`}
            </button>

          </div>

          {/* RIGHT */}
          <div className="bg-white p-8 shadow">

            <h2>Order Summary</h2>

            {cartItems.map((item, i) => (
              <div key={i} className="flex justify-between mb-2">
                <span>{item.name} x{item.quantity}</span>
                <span>₹{item.price * item.quantity}</span>
              </div>
            ))}

            <h3 className="mt-4 font-bold">Total: ₹{total}</h3>

          </div>

        </div>
      </div>
    </div>
  );
};

export default Checkout;